import React, { useEffect, useMemo, useState } from 'react';

import {
  ExclamationCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TrainingKind,
  TrainingListItem,
  TrainingStatus,
  TrainingsListParams,
  TrainingsListResponse,
} from 'models/Training';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../components/Layout/Layout';
import appI18n from '../../config/i18n';
import CreateTrainingModal from './Components/CreateTrainingModal';

import './Trainings.scss';

type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'trainings.pageSize';
const DEFAULT_COUNTRY = 'CL';
const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

const KIND_COLOR: Record<TrainingKind, string> = {
  in_person: 'blue',
  virtual: 'purple',
};

const STATUS_COLOR: Record<TrainingStatus, string> = {
  open: 'green',
  closed: 'default',
};

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

function Trainings() {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<TrainingListItem[]>([]);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [total, setTotal] = useState<number>(0);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Filtros aplicados (los que se mandan al backend)
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [term, setTerm] = useState<string>('');
  const [kindFilter, setKindFilter] = useState<TrainingKind | undefined>(
    undefined
  );
  const [statusFilter, setStatusFilter] = useState<TrainingStatus | undefined>(
    undefined
  );
  const [startsFrom, setStartsFrom] = useState<string | undefined>(undefined);
  const [startsTo, setStartsTo] = useState<string | undefined>(undefined);

  // Filtros en borrador (los que se editan dentro del popover)
  const [draftCountry, setDraftCountry] = useState<string>(DEFAULT_COUNTRY);
  const [draftKind, setDraftKind] = useState<TrainingKind | undefined>(
    undefined
  );
  const [draftStatus, setDraftStatus] = useState<TrainingStatus | undefined>(
    undefined
  );
  const [draftDateRange, setDraftDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setPage(1);
  }, [country, kindFilter, statusFilter, startsFrom, startsTo]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    pageSize,
    sortDir,
    country,
    kindFilter,
    statusFilter,
    startsFrom,
    startsTo,
  ]);

  const fetchData = async (silent = false) => {
    if (!silent) {
      setTableLoading(true);
    }

    const params: TrainingsListParams = {
      countryCode: country,
      page,
      pageSize,
      sort: 'starts_at',
      dir: sortDir,
    };
    if (kindFilter) params.kind = kindFilter;
    if (statusFilter) params.status = statusFilter;
    if (startsFrom) params.startsFrom = startsFrom;
    if (startsTo) params.startsTo = startsTo;

    const response = await LeadService.getTrainings(params);

    if (response.success) {
      const data = response.data as TrainingsListResponse;
      setDataSource(data.data);
      setTotal(data.totalRows);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.scene.errors.fetch_trainings'),
        3.5
      );
    }

    if (!silent) {
      setTableLoading(false);
    }
  };

  // Búsqueda client-side sobre name / hostName de la página actual
  const filteredDataSource = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return dataSource;
    return dataSource.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.hostName.toLowerCase().includes(q)
    );
  }, [dataSource, term]);

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleTableChange: TableProps<TrainingListItem>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }

    const sorterLocal = sorter as any;
    const order = sorterLocal?.order as 'ascend' | 'descend' | undefined;
    if (order) {
      setSortDir(order === 'descend' ? 'desc' : 'asc');
    } else {
      setSortDir('desc');
    }
  };

  const showDetailTraining = (row: TrainingListItem) => {
    navigate(`/portal-go/trainings/${row.id}`);
  };

  const handleToggleStatus = (row: TrainingListItem) => {
    const newStatus: TrainingStatus = row.status === 'open' ? 'closed' : 'open';
    const isClosing = newStatus === 'closed';
    const key = isClosing ? 'close' : 'open';

    Modal.confirm({
      title: t(`scenes.trainings.scene.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.trainings.scene.status_confirm.content_${key}`, {
        name: row.name,
      }),
      okText: t(`scenes.trainings.scene.status_confirm.ok_${key}`),
      cancelText: t('scenes.trainings.scene.status_confirm.cancel'),
      okButtonProps: { danger: isClosing },
      onOk: async () => {
        const response = await LeadService.updateTrainingStatus(row.id, {
          status: newStatus,
        });
        if (response.success) {
          messageApi.success(
            t(`scenes.trainings.scene.status_confirm.success_${key}`),
            2.5
          );
          // Actualización silent: mutamos solo el status del row en memoria.
          setDataSource((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r))
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t('scenes.trainings.scene.status_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftCountry(country);
      setDraftKind(kindFilter);
      setDraftStatus(statusFilter);
      setDraftDateRange(
        startsFrom && startsTo ? [dayjs(startsFrom), dayjs(startsTo)] : null
      );
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    setCountry(draftCountry);
    setKindFilter(draftKind);
    setStatusFilter(draftStatus);
    if (draftDateRange && draftDateRange[0] && draftDateRange[1]) {
      setStartsFrom(draftDateRange[0].format('YYYY-MM-DD'));
      setStartsTo(draftDateRange[1].format('YYYY-MM-DD'));
    } else {
      setStartsFrom(undefined);
      setStartsTo(undefined);
    }
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftCountry(DEFAULT_COUNTRY);
    setDraftKind(undefined);
    setDraftStatus(undefined);
    setDraftDateRange(null);
  };

  const activeFiltersCount =
    1 + // país siempre cuenta
    (kindFilter ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (startsFrom && startsTo ? 1 : 0);

  const getDropdownMenu = (row: TrainingListItem): MenuProps['items'] => {
    const isOpen = row.status === 'open';
    return [
      {
        key: 'view',
        label: (
          <span>
            <EyeOutlined style={{ marginRight: '8px' }} />
            {t('scenes.trainings.scene.dropdown.view_detail')}
          </span>
        ),
        onClick: () => showDetailTraining(row),
      },
      {
        key: 'toggle-status',
        label: (
          <span>
            {isOpen ? (
              <LockOutlined style={{ marginRight: '8px' }} />
            ) : (
              <UnlockOutlined style={{ marginRight: '8px' }} />
            )}
            {t(
              isOpen
                ? 'scenes.trainings.scene.dropdown.close'
                : 'scenes.trainings.scene.dropdown.open'
            )}
          </span>
        ),
        onClick: () => handleToggleStatus(row),
      },
    ];
  };

  const getCountryOptions = () =>
    COUNTRY_OPTIONS.map((opt) => {
      const code = opt.value.toLowerCase();
      return {
        value: opt.value,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <img
              src={`https://flagcdn.com/16x12/${code}.png`}
              srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
              width={16}
              height={12}
              alt=""
              style={{ display: 'block' }}
            />
            <span>
              {t(`scenes.trainings.scene.countries.${opt.value}`, opt.value)}
            </span>
          </span>
        ),
      };
    });

  const kindOptions: { value: TrainingKind; label: string }[] = [
    {
      value: 'in_person',
      label: t('scenes.trainings.scene.kinds.in_person'),
    },
    {
      value: 'virtual',
      label: t('scenes.trainings.scene.kinds.virtual'),
    },
  ];

  const statusOptions: { value: TrainingStatus; label: string }[] = [
    {
      value: 'open',
      label: t('scenes.trainings.scene.statuses.open'),
    },
    {
      value: 'closed',
      label: t('scenes.trainings.scene.statuses.closed'),
    },
  ];

  const formatDateCell = (isoDate: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(isoDate).format(`${datePart} h:mm A`);
  };

  const renderKind = (kind: TrainingKind) => (
    <Tag color={KIND_COLOR[kind]} style={{ marginRight: 0 }}>
      {t(`scenes.trainings.scene.kinds.${kind}`)}
    </Tag>
  );

  const renderStatus = (status: TrainingStatus) => (
    <Tag color={STATUS_COLOR[status]} style={{ marginRight: 0 }}>
      {t(`scenes.trainings.scene.statuses.${status}`)}
    </Tag>
  );

  const filtersContent = (
    <div style={{ width: 360 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('scenes.trainings.scene.filters.country_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftCountry}
            onChange={(value) => setDraftCountry(value)}
            options={getCountryOptions()}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.trainings.scene.filters.kind_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftKind}
            onChange={(value) =>
              setDraftKind(value as TrainingKind | undefined)
            }
            options={kindOptions}
            style={{ width: '100%' }}
            placeholder={t('scenes.trainings.scene.filters.kind_placeholder')}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.trainings.scene.filters.status_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            value={draftStatus}
            onChange={(value) =>
              setDraftStatus(value as TrainingStatus | undefined)
            }
            options={statusOptions}
            style={{ width: '100%' }}
            placeholder={t('scenes.trainings.scene.filters.status_placeholder')}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.trainings.scene.filters.starts_range_label')}
          style={{ marginBottom: 0 }}
        >
          <DatePicker.RangePicker
            value={draftDateRange as any}
            onChange={(range) =>
              setDraftDateRange(range as [Dayjs | null, Dayjs | null] | null)
            }
            style={{ width: '100%' }}
            allowClear
          />
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.trainings.scene.filters.clear_filters')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.trainings.scene.filters.apply_filters')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Layout title={''}>
      {contextHolder}

      <div className="trainings-scene">
        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.trainings.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.trainings.scene.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      <Space size={6}>
                        {t('scenes.trainings.scene.filters.advanced_filters')}
                      </Space>
                    </Button>
                  </Badge>
                </Popover>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  {t('scenes.trainings.scene.create.button')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card style={{ marginTop: '12px' }}>
          <Table<TrainingListItem>
            dataSource={filteredDataSource}
            bordered
            loading={tableLoading}
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.id}
            onChange={handleTableChange}
          >
            <Column
              title={t('scenes.trainings.scene.table.columns.training')}
              key="training"
              render={(row: TrainingListItem) => (
                <div>
                  <div className="training-cell-primary">
                    <button
                      type="button"
                      className="training-name-link"
                      onClick={() => showDetailTraining(row)}
                    >
                      {row.name}
                    </button>
                  </div>
                  <div className="training-cell-secondary">
                    {row.hostName} · {row.hostRole}
                  </div>
                </div>
              )}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.trainings.scene.table.columns.kind')}
              key="kind"
              render={(row: TrainingListItem) => renderKind(row.kind)}
            />

            <Column
              width={120}
              align="center"
              title={t('scenes.trainings.scene.table.columns.status')}
              key="status"
              render={(row: TrainingListItem) => renderStatus(row.status)}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.trainings.scene.table.columns.starts_at')}
              key="startsAt"
              sorter
              sortOrder={sortDir === 'asc' ? 'ascend' : 'descend'}
              render={(row: TrainingListItem) => (
                <span>{formatDateCell(row.startsAt)}</span>
              )}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.trainings.scene.table.columns.ends_at')}
              key="endsAt"
              render={(row: TrainingListItem) => (
                <span>{formatDateCell(row.endsAt)}</span>
              )}
            />

            <Column
              width={150}
              align="center"
              title={t('scenes.trainings.scene.table.columns.enrollment')}
              key="enrollment"
              render={(row: TrainingListItem) => (
                <span>
                  {row.enrolledCount} / {row.capacity}
                </span>
              )}
            />

            <Column
              width={180}
              align="center"
              title={t('scenes.trainings.scene.table.columns.created_at')}
              key="createdAt"
              render={(row: TrainingListItem) => (
                <span>
                  {row.createdAt ? formatDateCell(row.createdAt) : '—'}
                </span>
              )}
            />

            <Column
              width={70}
              align="center"
              title=""
              key="actions"
              render={(row: TrainingListItem) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      <CreateTrainingModal
        isOpen={isCreateModalOpen}
        defaultCountry={country}
        handleModalCancel={() => setIsCreateModalOpen(false)}
        onSaved={() => {
          messageApi.success(
            t('scenes.trainings.scene.create.messages.success'),
            3
          );
          fetchData(true);
        }}
      />
    </Layout>
  );
}

export default Trainings;
